<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">商品分類管理</h1>
      </div>
      <div class="header-actions">
        <button v-if="orderDirty" class="btn btn-success" :disabled="savingOrder" @click="saveOrder">
                  <i class="ri-save-line"></i> {{ savingOrder ? '儲存中...' : '儲存排序' }}
                </button>
                <button class="btn btn-primary" @click="openCreate(null)">
                  <i class="ri-add-line"></i> 新增一級分類
                </button>
      </div>
    </div>

    <div class="page-container">
    <div v-if="orderDirty" class="order-hint">
      <i class="ri-information-line"></i> 排序已變更，請點擊「儲存排序」以儲存。
    </div>

    <div v-if="loading" class="loading-state">載入中...</div>

    <div v-else class="category-tree">
      <div v-if="!categories.length" class="empty-state">
        <i class="ri-folder-open-line"></i>
        <p>尚未建立任何分類，請點擊「新增一級分類」開始</p>
      </div>

      <!-- ===== Level 1 Block ===== -->
      <div
        v-for="(lv1, l1Idx) in categories"
        :key="lv1.id"
        class="level1-block"
        :class="{ 'drag-over': dragOverL1 === l1Idx, dragging: dragL1 === l1Idx }"
        draggable="true"
        @dragstart="onL1DragStart(l1Idx, $event)"
        @dragover.prevent="onL1DragOver(l1Idx)"
        @dragleave="onL1DragLeave"
        @drop.prevent="onL1Drop(l1Idx)"
        @dragend="onL1DragEnd"
      >
        <!-- Level 1 Row -->
        <div class="category-row level-1">
          <div class="cat-left">
            <button
              v-if="lv1.children && lv1.children.length"
              class="btn-toggle"
              :title="isL1Expanded(lv1.id) ? '收合子分類' : '展開子分類'"
              @click.stop="toggleL1(lv1.id)"
            >
              <i :class="isL1Expanded(lv1.id) ? 'ri-arrow-down-s-line' : 'ri-arrow-right-s-line'"></i>
            </button>
            <span v-else class="toggle-spacer"></span>
            <span class="drag-handle" title="拖曳排序"><i class="ri-drag-move-2-line"></i></span>
            <span class="order-num">{{ l1Idx + 1 }}</span>
            <div class="cat-info">
              <i :class="['cat-icon', 'lv1-icon', isL1Expanded(lv1.id) ? 'ri-folder-open-line' : 'ri-folder-line']"></i>
              <span class="cat-name">{{ lv1.name }}</span>
              <span v-if="lv1.children && lv1.children.length" class="cat-badge">
                {{ lv1.children.length }} 子分類
              </span>
              <span :class="['status-tag', lv1.status]">{{ lv1.status === 'active' ? '啟用' : '停用' }}</span>
            </div>
          </div>
          <div class="cat-actions">
            <button class="btn-icon" title="上移" :disabled="l1Idx === 0" @click="moveL1(l1Idx, -1)">
              <i class="ri-arrow-up-s-line"></i>
            </button>
            <button class="btn-icon" title="下移" :disabled="l1Idx === categories.length - 1" @click="moveL1(l1Idx, 1)">
              <i class="ri-arrow-down-s-line"></i>
            </button>
            <button class="btn-icon" title="新增二級分類" @click="openCreate(lv1.id)">
              <i class="ri-folder-add-line"></i>
            </button>
            <button class="btn-icon" title="編輯" @click="openEdit(lv1)">
              <i class="ri-edit-line"></i>
            </button>
            <button class="btn-icon danger" title="刪除" @click="confirmDelete(lv1)">
              <i class="ri-delete-bin-line"></i>
            </button>
          </div>
        </div>

        <!-- ===== Level 2 Blocks（展開時顯示）===== -->
        <div v-show="isL1Expanded(lv1.id)" class="level2-container">
          <div
            v-for="(lv2, l2Idx) in (lv1.children || [])"
            :key="lv2.id"
            class="level2-block"
            :class="{
              'drag-over': dragOverL2 === `${lv1.id}-${l2Idx}`,
              dragging: dragL2Key === `${lv1.id}-${l2Idx}`
            }"
            draggable="true"
            @dragstart.stop="onL2DragStart(lv1, l2Idx, $event)"
            @dragover.prevent.stop="onL2DragOver(lv1.id, l2Idx)"
            @dragleave.stop="onL2DragLeave"
            @drop.prevent.stop="onL2Drop(lv1, l2Idx)"
            @dragend.stop="onL2DragEnd"
          >
            <!-- Level 2 Row -->
            <div class="category-row level-2">
              <div class="cat-left">
                <button
                  v-if="lv2.children && lv2.children.length"
                  class="btn-toggle"
                  :title="isL2Expanded(lv2.id) ? '收合子分類' : '展開子分類'"
                  @click.stop="toggleL2(lv2.id)"
                >
                  <i :class="isL2Expanded(lv2.id) ? 'ri-arrow-down-s-line' : 'ri-arrow-right-s-line'"></i>
                </button>
                <span v-else class="toggle-spacer"></span>
                <span class="drag-handle" title="拖曳排序"><i class="ri-drag-move-2-line"></i></span>
                <span class="order-num">{{ l2Idx + 1 }}</span>
                <div class="cat-info">
                  <i class="ri-price-tag-3-line cat-icon lv2-icon"></i>
                  <span class="cat-name">{{ lv2.name }}</span>
                  <span v-if="lv2.children && lv2.children.length" class="cat-badge">
                    {{ lv2.children.length }} 子分類
                  </span>
                  <span :class="['status-tag', lv2.status]">{{ lv2.status === 'active' ? '啟用' : '停用' }}</span>
                </div>
              </div>
              <div class="cat-actions">
                <button class="btn-icon" title="上移" :disabled="l2Idx === 0" @click="moveL2(lv1, l2Idx, -1)">
                  <i class="ri-arrow-up-s-line"></i>
                </button>
                <button class="btn-icon" title="下移" :disabled="l2Idx === (lv1.children.length - 1)" @click="moveL2(lv1, l2Idx, 1)">
                  <i class="ri-arrow-down-s-line"></i>
                </button>
                <button class="btn-icon" title="新增三級分類" @click="openCreate(lv2.id)">
                  <i class="ri-folder-add-line"></i>
                </button>
                <button class="btn-icon" title="編輯" @click="openEdit(lv2)">
                  <i class="ri-edit-line"></i>
                </button>
                <button class="btn-icon danger" title="刪除" @click="confirmDelete(lv2)">
                  <i class="ri-delete-bin-line"></i>
                </button>
              </div>
            </div>

            <!-- Level 3 Rows（展開時顯示）-->
            <div v-show="isL2Expanded(lv2.id)">
              <div
                v-for="(lv3, l3Idx) in (lv2.children || [])"
                :key="lv3.id"
                class="category-row level-3"
                :class="{
                  'drag-over': dragOverL3 === `${lv2.id}-${l3Idx}`,
                  dragging: dragL3Key === `${lv2.id}-${l3Idx}`
                }"
                draggable="true"
                @dragstart.stop="onL3DragStart(lv2, l3Idx, $event)"
                @dragover.prevent.stop="onL3DragOver(lv2.id, l3Idx)"
                @dragleave.stop="onL3DragLeave"
                @drop.prevent.stop="onL3Drop(lv2, l3Idx)"
                @dragend.stop="onL3DragEnd"
              >
                <div class="cat-left">
                  <span class="toggle-spacer"></span>
                  <span class="drag-handle" title="拖曳排序"><i class="ri-drag-move-2-line"></i></span>
                  <span class="order-num">{{ l3Idx + 1 }}</span>
                  <div class="cat-info">
                    <i class="ri-price-tag-line cat-icon lv3-icon"></i>
                    <span class="cat-name">{{ lv3.name }}</span>
                    <span :class="['status-tag', lv3.status]">{{ lv3.status === 'active' ? '啟用' : '停用' }}</span>
                  </div>
                </div>
                <div class="cat-actions">
                  <button class="btn-icon" title="上移" :disabled="l3Idx === 0" @click="moveL3(lv2, l3Idx, -1)">
                    <i class="ri-arrow-up-s-line"></i>
                  </button>
                  <button class="btn-icon" title="下移" :disabled="l3Idx === (lv2.children.length - 1)" @click="moveL3(lv2, l3Idx, 1)">
                    <i class="ri-arrow-down-s-line"></i>
                  </button>
                  <button class="btn-icon" title="編輯" @click="openEdit(lv3)">
                    <i class="ri-edit-line"></i>
                  </button>
                  <button class="btn-icon danger" title="刪除" @click="confirmDelete(lv3)">
                    <i class="ri-delete-bin-line"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 新增 / 編輯 Modal -->
    <div v-if="showModal" class="modal-overlay" @click.self="closeModal">
      <div class="modal-box">
        <div class="modal-header">
          <h3>{{ modalTitle }}</h3>
          <button class="btn-close" @click="closeModal"><i class="ri-close-line"></i></button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label class="required">分類名稱</label>
            <input
              v-model="form.name"
              type="text"
              class="form-control"
              placeholder="請輸入分類名稱"
              maxlength="100"
              @keydown.enter="handleSubmit"
            />
          </div>
          <div v-if="showParentSelect" class="form-group">
            <label>所屬上層分類</label>
            <select v-model="form.parentId" class="form-control">
              <option :value="null">無（設為一級分類）</option>
              <optgroup label="── 一級分類（建立後為二級）──">
                <option v-for="p in topLevelCategories" :key="p.id" :value="p.id">
                  {{ p.name }}
                </option>
              </optgroup>
              <optgroup v-if="secondLevelCategories.length" label="── 二級分類（建立後為三級）──">
                <option v-for="p in secondLevelCategories" :key="p.id" :value="p.id">
                  {{ p.parentName }} › {{ p.name }}
                </option>
              </optgroup>
            </select>
          </div>
          <div class="form-group">
            <label>狀態</label>
            <select v-model="form.status" class="form-control">
              <option value="active">啟用</option>
              <option value="inactive">停用</option>
            </select>
          </div>
          <div class="form-group">
            <label class="required">URL Slug</label>
            <input v-model="form.storefrontSlug" class="form-control" placeholder="例如 laptop、web-design" maxlength="200" />
            <p style="font-size:12px;color:#9ca3af;margin-top:2px;">前台分類網址。只能使用小寫英文、數字與連字號，且須含英文字母。</p>
          </div>
          <div class="form-group">
            <label>SEO 標題</label>
            <input v-model="form.seoTitle" class="form-control" placeholder="覆寫分類名稱（選填）" />
          </div>
          <div class="form-group">
            <label>SEO 描述</label>
            <textarea v-model="form.seoDescription" class="form-control" rows="2" placeholder="搜尋引擎描述（選填）"></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="closeModal">取消</button>
          <button class="btn btn-primary" :disabled="saving" @click="handleSubmit">
            {{ saving ? '儲存中...' : '儲存' }}
          </button>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, reactive, onMounted } from 'vue';
import { productService } from '@/api/modules/products.js';

const loading = ref(false);
const saving = ref(false);
const savingOrder = ref(false);
const orderDirty = ref(false);
const categories = ref([]);
const showModal = ref(false);
const editingId = ref(null);
const parentIdForNew = ref(null);

const form = ref({ name: '', parentId: null, status: 'active', storefrontSlug: '', seoTitle: '', seoDescription: '' });

// Level 1 drag state
const dragL1 = ref(null);
const dragOverL1 = ref(null);

// Level 2 drag state
const dragL2Key = ref(null);
const dragOverL2 = ref(null);
let _dragL2Parent = null;
let _dragL2Idx = null;

// Level 3 drag state
const dragL3Key = ref(null);
const dragOverL3 = ref(null);
let _dragL3Parent = null;
let _dragL3Idx = null;

// 展開/收合狀態
const expandedL1Ids = reactive(new Set());
const expandedL2Ids = reactive(new Set());

function isL1Expanded(id) { return expandedL1Ids.has(id); }
function toggleL1(id) {
  if (expandedL1Ids.has(id)) expandedL1Ids.delete(id);
  else expandedL1Ids.add(id);
}
function isL2Expanded(id) { return expandedL2Ids.has(id); }
function toggleL2(id) {
  if (expandedL2Ids.has(id)) expandedL2Ids.delete(id);
  else expandedL2Ids.add(id);
}

const topLevelCategories = computed(() => categories.value);

const secondLevelCategories = computed(() =>
  categories.value.flatMap(lv1 => lv1.children || [])
);

const showParentSelect = computed(() => {
  if (editingId.value) return form.value.parentId !== null;
  return parentIdForNew.value !== null;
});

const modalTitle = computed(() => {
  if (editingId.value) return '編輯分類';
  if (!parentIdForNew.value) return '新增一級分類';
  const isL1Parent = categories.value.some(c => c.id === parentIdForNew.value);
  return isL1Parent ? '新增二級分類' : '新增三級分類';
});

async function loadCategories() {
  loading.value = true;
  orderDirty.value = false;
  try {
    const res = await productService.getCategoriesTree();
    categories.value = res.data || [];
    // 預設展開所有有子分類的節點
    expandedL1Ids.clear();
    expandedL2Ids.clear();
    (res.data || []).forEach(lv1 => {
      if (lv1.children?.length) {
        expandedL1Ids.add(lv1.id);
        lv1.children.forEach(lv2 => {
          if (lv2.children?.length) expandedL2Ids.add(lv2.id);
        });
      }
    });
  } catch (e) {
    alert('載入分類失敗：' + (e.response?.data?.message || e.message));
  } finally {
    loading.value = false;
  }
}

// ===== Level 1 ordering =====
function moveL1(idx, dir) {
  const arr = categories.value;
  const t = idx + dir;
  if (t < 0 || t >= arr.length) return;
  [arr[idx], arr[t]] = [arr[t], arr[idx]];
  orderDirty.value = true;
}

function onL1DragStart(idx, e) { dragL1.value = idx; e.dataTransfer.effectAllowed = 'move'; }
function onL1DragOver(idx) { if (dragL1.value === null) return; dragOverL1.value = idx; }
function onL1DragLeave() { dragOverL1.value = null; }
function onL1Drop(targetIdx) {
  const from = dragL1.value;
  if (from === null || from === targetIdx) { onL1DragEnd(); return; }
  const arr = categories.value;
  const item = arr.splice(from, 1)[0];
  arr.splice(targetIdx, 0, item);
  orderDirty.value = true;
  onL1DragEnd();
}
function onL1DragEnd() { dragL1.value = null; dragOverL1.value = null; }

// ===== Level 2 ordering =====
function moveL2(lv1, idx, dir) {
  const arr = lv1.children;
  const t = idx + dir;
  if (t < 0 || t >= arr.length) return;
  [arr[idx], arr[t]] = [arr[t], arr[idx]];
  orderDirty.value = true;
}

function onL2DragStart(lv1, idx, e) {
  dragL2Key.value = `${lv1.id}-${idx}`;
  _dragL2Parent = lv1;
  _dragL2Idx = idx;
  e.dataTransfer.effectAllowed = 'move';
}
function onL2DragOver(lv1Id, idx) { if (!dragL2Key.value) return; dragOverL2.value = `${lv1Id}-${idx}`; }
function onL2DragLeave() { dragOverL2.value = null; }
function onL2Drop(targetLv1, targetIdx) {
  if (!dragL2Key.value || _dragL2Parent?.id !== targetLv1.id) { onL2DragEnd(); return; }
  const from = _dragL2Idx;
  if (from === targetIdx) { onL2DragEnd(); return; }
  const arr = targetLv1.children;
  const item = arr.splice(from, 1)[0];
  arr.splice(targetIdx, 0, item);
  orderDirty.value = true;
  onL2DragEnd();
}
function onL2DragEnd() { dragL2Key.value = null; dragOverL2.value = null; _dragL2Parent = null; _dragL2Idx = null; }

// ===== Level 3 ordering =====
function moveL3(lv2, idx, dir) {
  const arr = lv2.children;
  const t = idx + dir;
  if (t < 0 || t >= arr.length) return;
  [arr[idx], arr[t]] = [arr[t], arr[idx]];
  orderDirty.value = true;
}

function onL3DragStart(lv2, idx, e) {
  dragL3Key.value = `${lv2.id}-${idx}`;
  _dragL3Parent = lv2;
  _dragL3Idx = idx;
  e.dataTransfer.effectAllowed = 'move';
}
function onL3DragOver(lv2Id, idx) { if (!dragL3Key.value) return; dragOverL3.value = `${lv2Id}-${idx}`; }
function onL3DragLeave() { dragOverL3.value = null; }
function onL3Drop(targetLv2, targetIdx) {
  if (!dragL3Key.value || _dragL3Parent?.id !== targetLv2.id) { onL3DragEnd(); return; }
  const from = _dragL3Idx;
  if (from === targetIdx) { onL3DragEnd(); return; }
  const arr = targetLv2.children;
  const item = arr.splice(from, 1)[0];
  arr.splice(targetIdx, 0, item);
  orderDirty.value = true;
  onL3DragEnd();
}
function onL3DragEnd() { dragL3Key.value = null; dragOverL3.value = null; _dragL3Parent = null; _dragL3Idx = null; }

// ===== Save order =====
async function saveOrder() {
  savingOrder.value = true;
  try {
    const updates = [];
    categories.value.forEach((lv1, i) => {
      updates.push(productService.updateCategory(lv1.id, { sortOrder: i + 1 }));
      (lv1.children || []).forEach((lv2, j) => {
        updates.push(productService.updateCategory(lv2.id, { sortOrder: j + 1 }));
        (lv2.children || []).forEach((lv3, k) => {
          updates.push(productService.updateCategory(lv3.id, { sortOrder: k + 1 }));
        });
      });
    });
    await Promise.all(updates);
    orderDirty.value = false;
  } catch (e) {
    alert('儲存排序失敗：' + (e.response?.data?.message || e.message));
  } finally {
    savingOrder.value = false;
  }
}

// ===== CRUD =====
function openCreate(parentId) {
  editingId.value = null;
  parentIdForNew.value = parentId;
  form.value = { name: '', parentId: parentId || null, status: 'active', storefrontSlug: '', seoTitle: '', seoDescription: '' };
  showModal.value = true;
}

function openEdit(category) {
  editingId.value = category.id;
  parentIdForNew.value = null;
  form.value = {
    name: category.name,
    parentId: category.parentId || null,
    status: category.status,
    storefrontSlug: category.storefrontSlug || category.storefront_slug || '',
    seoTitle: category.seoTitle || category.seo_title || '',
    seoDescription: category.seoDescription || category.seo_description || '',
  };
  showModal.value = true;
}

function closeModal() { showModal.value = false; }

function normalizeCategorySlug(raw) {
  return String(raw || '')
    .normalize('NFKC')
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/[\s-]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

async function handleSubmit() {
  if (!form.value.name.trim()) { alert('請輸入分類名稱'); return; }
  const slug = normalizeCategorySlug(form.value.storefrontSlug);
  if (!/[a-z]/.test(slug)) {
    alert('URL Slug 須含英文字母，只能使用小寫英文、數字與連字號');
    return;
  }
  form.value.storefrontSlug = slug;
  saving.value = true;
  try {
    if (editingId.value) {
      await productService.updateCategory(editingId.value, form.value);
    } else {
      await productService.createCategory(form.value);
    }
    closeModal();
    await loadCategories();
  } catch (e) {
    alert('儲存失敗：' + (e.response?.data?.message || e.message));
  } finally {
    saving.value = false;
  }
}

async function confirmDelete(category) {
  const hasChildren = category.children && category.children.length > 0;
  const isL1 = !category.parentId;
  const isL2 = category.parentId && categories.value.some(c => c.id === category.parentId);
  const levelLabel = isL1 ? '一級' : isL2 ? '二級' : '三級';

  let msg = `確定刪除${levelLabel}分類「${category.name}」？`;
  if (hasChildren) {
    msg += `\n此分類下有 ${category.children.length} 個子分類，刪除後子分類將自動升至上層。`;
  }
  msg += '\n若有商品使用此分類，將無法刪除。';

  if (!confirm(msg)) return;
  try {
    await productService.deleteCategory(category.id);
    await loadCategories();
  } catch (e) {
    alert('刪除失敗：' + (e.response?.data?.message || e.message));
  }
}

onMounted(loadCategories);
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';



.order-hint {
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 6px;
  padding: 8px 14px;
  font-size: 13px;
  color: #92400e;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 6px;
}

/* ===== Tree Container ===== */
.category-tree {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* ===== Level 1 Block ===== */
.level1-block {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  transition: box-shadow 0.15s;
  cursor: default;
}

.level1-block.drag-over {
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.25);
}

.level1-block.dragging {
  opacity: 0.5;
}

/* ===== Level 2 Block ===== */
.level2-block {
  border-top: 1px solid #e9ecef;
  transition: background 0.1s;
}

.level2-block.drag-over > .category-row.level-2 {
  background: #eff6ff;
}

.level2-block.dragging {
  opacity: 0.45;
}

/* ===== Category Rows (shared) ===== */
.category-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  gap: 8px;
}

.category-row.level-1 {
  background: #f3f4f6;
}

.category-row.level-2 {
  background: #fff;
  padding-left: 36px;
  border-bottom: 1px solid #f1f5f9;
}

.level2-block:last-child > .category-row.level-2:only-child,
.level2-block:last-child > .category-row.level-2:last-of-type {
  border-bottom: none;
}

.category-row.level-3 {
  background: #f9fafb;
  padding-left: 60px;
  border-top: 1px solid #f1f5f9;
}

.category-row.level-3.drag-over {
  background: #eff6ff;
}

.category-row.level-3.dragging {
  opacity: 0.4;
}

/* ===== Toggle button ===== */
.btn-toggle {
  width: 22px;
  height: 22px;
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 15px;
  flex-shrink: 0;
  border-radius: 4px;
  transition: color 0.15s, background 0.15s;
  padding: 0;
}

.btn-toggle:hover {
  color: #4b5563;
  background: rgba(0, 0, 0, 0.05);
}

.toggle-spacer {
  width: 22px;
  flex-shrink: 0;
}

/* ===== Row content ===== */
.cat-left {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  min-width: 0;
}

.drag-handle {
  cursor: grab;
  color: #c4c9d4;
  font-size: 16px;
  flex-shrink: 0;
  padding: 2px;
  user-select: none;
}

.drag-handle:hover {
  color: #9ca3af;
}

.order-num {
  font-size: 11px;
  color: #9ca3af;
  min-width: 18px;
  text-align: center;
  flex-shrink: 0;
}

.cat-info {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.cat-icon {
  font-size: 15px;
  flex-shrink: 0;
}

.lv1-icon { color: #4b5563; }
.lv2-icon { color: #6b7280; }
.lv3-icon { color: #9ca3af; }

.cat-name {
  font-weight: 500;
  color: #1f2937;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.category-row.level-2 .cat-name,
.category-row.level-3 .cat-name {
  font-weight: 400;
}

.cat-badge {
  font-size: 11px;
  color: #6b7280;
  background: #e5e7eb;
  padding: 1px 7px;
  border-radius: 10px;
  white-space: nowrap;
  flex-shrink: 0;
}

.status-tag {
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 10px;
  font-weight: 500;
  white-space: nowrap;
  flex-shrink: 0;
}

.status-tag.active { background: #d1fae5; color: #065f46; }
.status-tag.inactive { background: #f3f4f6; color: #6b7280; }

/* ===== Actions ===== */
.cat-actions {
  display: flex;
  gap: 4px;
  flex-shrink: 0;
}

.btn-icon {
  width: 30px;
  height: 30px;
  border: 1px solid #e5e7eb;
  background: #fff;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  font-size: 14px;
  transition: all 0.15s;
}

.btn-icon:hover:not(:disabled) {
  background: #f3f4f6;
  color: #374151;
}

.btn-icon:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.btn-icon.danger:hover:not(:disabled) {
  background: #fef2f2;
  color: #dc2626;
  border-color: #fca5a5;
}

/* ===== Empty / Loading ===== */
.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #9ca3af;
}

.empty-state i { font-size: 48px; display: block; margin-bottom: 12px; }

.loading-state { padding: 40px; text-align: center; color: #9ca3af; }

/* ===== Modal ===== */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-box {
  background: #fff;
  border-radius: 12px;
  width: 440px;
  max-width: 95vw;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px 16px;
  border-bottom: 1px solid #f1f5f9;
}

.modal-header h3 { margin: 0; font-size: 16px; font-weight: 600; }

.btn-close { background: none; border: none; font-size: 20px; cursor: pointer; color: #9ca3af; }

.modal-body { padding: 20px 24px; }

.form-group { margin-bottom: 16px; }

.form-group label {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 6px;
}

.form-group label.required::after { content: ' *'; color: #ef4444; }

.form-control {
  width: 100%;
  height: 38px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 0 10px;
  font-size: 14px;
  box-sizing: border-box;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px 20px;
  border-top: 1px solid #f1f5f9;
}

.btn {
  padding: 8px 18px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  border: none;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;
}

.btn-primary { background: #3b82f6; color: #fff; }
.btn-primary:hover:not(:disabled) { background: #2563eb; }
.btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
.btn-secondary { background: #f3f4f6; color: #374151; border: 1px solid #e5e7eb; }
.btn-success { background: #10b981; color: #fff; }
.btn-success:hover:not(:disabled) { background: #059669; }
.btn-success:disabled { opacity: 0.6; cursor: not-allowed; }
</style>
